import React, { FC, Fragment, useState } from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { Order } from 'models/order';
import { useTranslation } from 'react-i18next';
import DeliveryScheduleTableItem from 'components/delivery-schedule/delivery-schedule-table-item.component';
import PageTitle from 'components/shared/page-title/page-title.component';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import Loader from 'components/shared/loader/loader.component';
import { DeliveryScheduleLateOrdersTemplate, removeFromScheduleTemplate } from 'apollo/templates/delivery-schedule';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import SendToClinicDialog from 'components/orders/order-details/send-to-clinic-dialog.component';
import ChangeInternalDeliveryDateDialog from 'components/orders/order-details/change-internal-delivery-date-dialog.component';
import ChangeExternalDeliveryDateDialog from 'components/orders/order-details/change-external-delivery-date-dialog.component';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { OrderSortColumn, SortDirection } from 'components/delivery-schedule/use-order-sort.hook';
import InfiniteScroll from 'react-infinite-scroll-component';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';

type DeliveryScheduleLateOrdersProps = {
    onBackArrow: () => void;
    isExternal: boolean;
};
const DeliveryScheduleLateOrders: FC<DeliveryScheduleLateOrdersProps> = ({
    onBackArrow,
    isExternal,
}: DeliveryScheduleLateOrdersProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'delivery-schedule']);
    const [orders, setOrders] = useState<Order[]>([]);
    const [lastOffset, setLastOffset] = useState(0);
    const [sortColumn, setSortColumn] = useState<OrderSortColumn>('deliveryDate');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [openSentByLabDialog, setOpenSentByLabDialog] = useState(false);
    const [openRemoveFromScheduleDialog, setOpenRemoveFromScheduleDialog] = useState(false);
    const [changeInternalDeliveryDateDialogOpen, setChangeInternalDeliveryDateDialogOpen] = useState(false);
    const [changeExternalDeliveryDateDialogOpen, setChangeExternalDeliveryDateDialogOpen] = useState(false);
    const [order, setOrder] = useState<Order>({} as Order);

    const { loading, error, fetchMore, networkStatus } = useQuery(DeliveryScheduleLateOrdersTemplate, {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        variables: { isExternal, limit: 20, offset: 0, sortBy: sortColumn, sortDirection },
        onCompleted: ({ deliveryScheduleLateOrders }) => {
            setOrders(deliveryScheduleLateOrders);
            setLastOffset(deliveryScheduleLateOrders.length);
        },
    });

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({ variables: { offset: lastOffset } });
        }
    };

    const handleSort = (column: OrderSortColumn) => {
        const newDirection = column === sortColumn && sortDirection === 'asc' ? 'desc' : 'asc';
        setOrders([]);
        setLastOffset(0);
        setSortColumn(column);
        setSortDirection(newDirection);
    };

    // Fix for infinite scroll not triggering on tall screens.
    useFixMissingScroll(handleFetchMore, orders?.length || 0);

    const [removeFromSchedule, { loading: removeFromScheduleLoading, error: removeFromScheduleError }] = useMutation(
        removeFromScheduleTemplate,
        {
            onCompleted: ({ removeFromSchedule: order }) => {
                const ordersArr = JSON.parse(JSON.stringify(orders));
                const index = ordersArr.findIndex((ord: Order) => ord._id === order._id);
                if (index !== -1) {
                    ordersArr.splice(index, 1, {
                        ...ordersArr[index],
                        deliveryDetails: { ...ordersArr[index].deliveryDetails, ...order.deliveryDetails },
                    });
                    setOrders(ordersArr);
                }
                setOpenRemoveFromScheduleDialog(false);
            },
        },
    );

    const handleSentByClinicDialog = (order: Order) => {
        setOrder(order);
        setOpenSentByLabDialog(true);
    };

    const handleRemoveFromScheduleDialog = (order: Order) => {
        setOrder(order);
        setOpenRemoveFromScheduleDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenSentByLabDialog(false);
        setOpenRemoveFromScheduleDialog(false);
        setChangeInternalDeliveryDateDialogOpen(false);
        setChangeExternalDeliveryDateDialogOpen(false);
        setOrder({} as Order);
    };

    const handleInternalDeliveryDate = (order: Order) => {
        setOrder(order);
        setChangeInternalDeliveryDateDialogOpen(true);
    };

    const handleExternalDeliveryDate = (order: Order) => {
        setOrder(order);
        setChangeExternalDeliveryDateDialogOpen(true);
    };

    const handleUpdateList = (order: Order) => {
        const ordersArr = JSON.parse(JSON.stringify(orders));
        const index = ordersArr.findIndex((ord: Order) => ord._id === order._id);
        if (index !== -1) {
            ordersArr.splice(index, 1);
            setOrders(ordersArr);
        }
    };

    if (loading && networkStatus !== NetworkStatus.fetchMore) {
        return <Loader />;
    }

    return (
        <Fragment>
            {removeFromScheduleLoading ? <Loader /> : undefined}
            {error || removeFromScheduleError ? (
                <AlertSnackbar type="error" message={(error || removeFromScheduleError)?.message || ''} />
            ) : undefined}
            <PageTitle
                headingStyle="h1"
                title={translate('delivery-schedule:LATE_ORDERS')}
                showBackButton={true}
                onBackArrow={onBackArrow}
            />
            <TableContainer component={Paper}>
                <InfiniteScroll
                    style={{ padding: 0 }}
                    dataLength={orders ? orders.length : 0}
                    next={handleFetchMore}
                    throttleTimeInMs={75}
                    hasMore={true}
                    loader={undefined}
                >
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('common:ORDER_ID')}</TableCell>
                                <TableCell sortDirection={sortColumn === 'patient' ? sortDirection : false}>
                                    <TableSortLabel
                                        active={sortColumn === 'patient'}
                                        direction={sortColumn === 'patient' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('patient' as OrderSortColumn)}
                                    >
                                        {translate('common:PATIENT')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={sortColumn === 'destination' ? sortDirection : false}>
                                    <TableSortLabel
                                        active={sortColumn === 'destination'}
                                        direction={sortColumn === 'destination' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('destination' as OrderSortColumn)}
                                    >
                                        {translate('delivery-schedule:DELIVERY_TO')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={sortColumn === 'dentist' ? sortDirection : false}>
                                    <TableSortLabel
                                        active={sortColumn === 'dentist'}
                                        direction={sortColumn === 'dentist' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('dentist' as OrderSortColumn)}
                                    >
                                        {translate('common:DENTIST')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={sortColumn === 'deliveryDate' ? sortDirection : false}>
                                    <TableSortLabel
                                        active={sortColumn === 'deliveryDate'}
                                        direction={sortColumn === 'deliveryDate' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('deliveryDate' as OrderSortColumn)}
                                    >
                                        {translate('orders:DELIVERY_DATE')}
                                    </TableSortLabel>
                                </TableCell>
                                {isExternal && (
                                    <TableCell
                                        sortDirection={sortColumn === 'externalDeliveryDate' ? sortDirection : false}
                                    >
                                        <TableSortLabel
                                            active={sortColumn === 'externalDeliveryDate'}
                                            direction={sortColumn === 'externalDeliveryDate' ? sortDirection : 'asc'}
                                            onClick={() => handleSort('externalDeliveryDate' as OrderSortColumn)}
                                        >
                                            {translate('orders:EXTERNAL_DELIVERY_DATE')}
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                <TableCell sortDirection={sortColumn === 'workflowStep' ? sortDirection : false}>
                                    <TableSortLabel
                                        active={sortColumn === 'workflowStep'}
                                        direction={sortColumn === 'workflowStep' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('workflowStep' as OrderSortColumn)}
                                    >
                                        {translate('orders:WORKFLOW_STEP')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>{translate('orders:CONSTRUCTIONS')}</TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orders.map((order: Order) => (
                                <DeliveryScheduleTableItem
                                    key={order._id}
                                    order={order}
                                    onSentByClinicDialog={handleSentByClinicDialog}
                                    onRemoveFromScheduleDialog={handleRemoveFromScheduleDialog}
                                    onChangeInternalDeliveryDate={handleInternalDeliveryDate}
                                    onChangeExternalDeliveryDate={handleExternalDeliveryDate}
                                    isExternal={isExternal}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </InfiniteScroll>
            </TableContainer>

            {openSentByLabDialog ? (
                <SendToClinicDialog
                    isUpdateCache={true}
                    onUpdate={handleUpdateList}
                    open={openSentByLabDialog}
                    onClose={handleCloseDialog}
                    orderId={order._id}
                />
            ) : undefined}

            <ConfirmDialog
                open={openRemoveFromScheduleDialog}
                handleClose={handleCloseDialog}
                dialogContent={translate('delivery-schedule:REMOVE_EXTERNAL_DEADLINE_DESC')}
                handleConfirm={() => removeFromSchedule({ variables: { orderId: order._id } })}
            />

            <ChangeInternalDeliveryDateDialog
                isUpdateCache={true}
                onUpdate={handleUpdateList}
                open={changeInternalDeliveryDateDialogOpen}
                onClose={handleCloseDialog}
                order={order}
            />
            {order.deliveryDetails ? (
                <ChangeExternalDeliveryDateDialog
                    onUpdate={handleUpdateList}
                    open={changeExternalDeliveryDateDialogOpen}
                    onClose={handleCloseDialog}
                    order={order}
                />
            ) : undefined}
        </Fragment>
    );
};

export default DeliveryScheduleLateOrders;
